import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import type { FichaInsumosExportPayload } from './exportFichaTecnicaInsumosXlsx'

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0)
}

function formatMoneyRs(n: number): string {
  return `R$ ${formatMoney(n)}`
}

const PRINT_CSS = `
  .ft-pdf-root {
    width: 794px;
    box-sizing: border-box;
    padding: 24px;
    background: #fff;
    color: #132615;
    font-family: "Source Sans 3", "Segoe UI", sans-serif;
    font-size: 13px;
    line-height: 1.35;
  }
  .ft-pdf-title {
    background: #2e7d32;
    color: #fff;
    font-weight: 800;
    font-size: 16px;
    letter-spacing: 0.02em;
    text-align: center;
    padding: 10px 12px;
    border: 1px solid #1b5e20;
  }
  .ft-pdf-meta {
    border: 1px solid #cfe0cf;
    border-top: 0;
    padding: 10px 12px;
    background: #fff;
  }
  .ft-pdf-meta strong { font-weight: 700; }
  .ft-pdf-section {
    margin-top: 14px;
    background: #2e7d32;
    color: #fff;
    font-weight: 700;
    padding: 6px 10px;
    border: 1px solid #1b5e20;
  }
  .ft-pdf-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .ft-pdf-table th {
    background: #2e7d32;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    padding: 7px 8px;
    border: 1px solid #1b5e20;
    text-align: left;
  }
  .ft-pdf-table td {
    border: 1px solid #cfe0cf;
    padding: 6px 8px;
    vertical-align: top;
  }
  .ft-pdf-table .num { text-align: right; font-variant-numeric: tabular-nums; }
  .ft-pdf-table .total-row {
    background: #E2F0D9;
    font-weight: 700;
  }
  .ft-pdf-modo {
    margin-top: 14px;
    border: 1px solid #cfe0cf;
    background: #E2F0D9;
    padding: 10px 12px;
    min-height: 72px;
    white-space: pre-wrap;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    text-align: left;
  }
  .ft-pdf-modo strong {
    display: block;
    margin-bottom: 6px;
    align-self: flex-start;
  }
  .ft-pdf-modo-body {
    width: 100%;
    white-space: pre-wrap;
    text-align: left;
  }
  .ft-pdf-footer {
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0;
    border: 1px solid #cfe0cf;
    background: #E2F0D9;
    font-weight: 700;
  }
  .ft-pdf-footer div {
    padding: 8px 10px;
    border-right: 1px solid #cfe0cf;
  }
  .ft-pdf-footer div:last-child { border-right: 0; }
`

export function buildFichaInsumosHtml(ficha: FichaInsumosExportPayload): string {
  const ings = ficha.ingredientes
  const custoTotal = ings.reduce((s, i) => s + (Number(i.valorTotal) || 0), 0)
  const rowsHtml = (ings.length
    ? ings
    : [{ item: '—', un: '', qtdG: 0, valorUnitario: 0, valorTotal: 0 }]
  )
    .map(
      (ing) => `
      <tr>
        <td>${escapeHtml(ing.item || '')}</td>
        <td>${escapeHtml(ing.un || 'G')}</td>
        <td class="num">${round2(ing.qtdG)}</td>
        <td class="num">${formatMoneyRs(ing.valorUnitario)}</td>
        <td class="num">${formatMoneyRs(ing.valorTotal)}</td>
      </tr>`,
    )
    .join('')

  return `
    <style>${PRINT_CSS}</style>
    <div class="ft-pdf-root">
      <div class="ft-pdf-title">FICHA TÉCNICA DE PREPARAÇÃO</div>
      <div class="ft-pdf-meta">
        <div><strong>NOME DA PREPARAÇÃO:</strong> ${escapeHtml(ficha.nomePreparacao || '')}</div>
        <div style="margin-top:6px"><strong>PROFISSIONAL:</strong> ${escapeHtml(ficha.profissional || '')}</div>
      </div>
      <div class="ft-pdf-section">INSUMOS</div>
      <table class="ft-pdf-table">
        <thead>
          <tr>
            <th style="width:38%">ITEM</th>
            <th style="width:8%">UN</th>
            <th style="width:12%">QTD (g)</th>
            <th style="width:21%">Valor Unitário (R$)</th>
            <th style="width:21%">Valor Total (R$)</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
          <tr class="total-row">
            <td colspan="4">TOTAL DOS INSUMOS</td>
            <td class="num">${formatMoneyRs(custoTotal)}</td>
          </tr>
        </tbody>
      </table>
      <div class="ft-pdf-modo">
        <strong>Modo de Preparo:</strong>
        <div class="ft-pdf-modo-body">${escapeHtml(ficha.modoPreparo?.trim() || '')}</div>
      </div>
      <div class="ft-pdf-footer">
        <div>Rendimento: 1 porção</div>
        <div>Custo total: ${formatMoneyRs(custoTotal)}</div>
        <div>Custo/porção: ${formatMoneyRs(custoTotal)}</div>
      </div>
    </div>
  `
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export async function downloadFichaTecnicaInsumosPdf(
  ficha: FichaInsumosExportPayload,
  filename: string,
) {
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;background:#fff;z-index:-1;pointer-events:none;'
  const wrap = document.createElement('div')
  wrap.innerHTML = buildFichaInsumosHtml(ficha)
  host.appendChild(wrap)
  document.body.appendChild(host)

  try {
    const target = wrap.querySelector('.ft-pdf-root') as HTMLElement
    const canvas = await html2canvas(target, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    })
    const img = canvas.toDataURL('image/png')
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()
    const margin = 8
    const maxW = pageW - margin * 2
    const maxH = pageH - margin * 2
    const ratio = Math.min(maxW / canvas.width, maxH / canvas.height)
    const w = canvas.width * ratio
    const h = canvas.height * ratio
    pdf.addImage(img, 'PNG', margin + (maxW - w) / 2, margin, w, h)
    const safeName = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`
    pdf.save(safeName)
  } finally {
    host.remove()
  }
}
